import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, Sparkles } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Spinner } from '../../components/ui/Spinner';
import { useElapsedSeconds } from '../../lib/useElapsedSeconds';
import { draftBrandVoiceProfile, type VoiceProfileDraft } from '../franchise/api';

// ⚠️⚠️ THE VOICE-PROFILE DRAFTER — the control that reads a brand's past copy and
// PROPOSES a description of how it sounds.
//
// THE REQUIREMENT it exists for (Scott, 2026-08-27): "I wouldn't want the system
// to copy exactly what we've done, but use as a guide. The system should have the
// freedom to come up with new copy that it thinks will be high performing."
//
// ⚠️ WHAT COMES BACK IS A CHARACTERISATION, NOT EXAMPLES, and the copy on screen
// says so — because the natural thing for an AM to assume is that "drafted from
// past copy" means "here are your best past ads", which is precisely the thing
// the design refuses. The ads are shown to that one call and never stored, so at
// generation time there are no angles available to lift.
//
// ⚠️ IT NEVER SAVES. The proposal lands in the field; the AM edits it and presses
// the form's own save. That human edit is the real filter, and it matters more
// each month: once campaign copy is AI-written, the past copy this reads
// increasingly IS AI output.
//
// ⚠️ ON-DEMAND ONLY. No effect, no query — it reads a brand's whole campaign
// history and calls Opus.

export function BrandVoiceDrafter({
  accountId,
  hasProfileText,
  onProposed,
}: {
  /** The BRAND account. The drafter reads its campaigns. */
  accountId: string;
  /** Whether the field already has something in it — changes the warning. */
  hasProfileText: boolean;
  /** Puts the proposal in the field and stamps the provenance. Never saves. */
  onProposed: (draft: VoiceProfileDraft) => void;
}) {
  const [report, setReport] = useState<VoiceProfileDraft | null>(null);
  const [confirming, setConfirming] = useState(false);

  const draft = useMutation({
    mutationFn: () => draftBrandVoiceProfile(accountId),
    onSuccess: (data) => {
      setReport(data);
      setConfirming(false);
      onProposed(data);
    },
  });

  const waiting = useElapsedSeconds(draft.isPending);
  const error = draft.isError ? (draft.error as Error).message : null;

  // ⚠️ Replacing an authored profile is a real loss — somebody wrote it — so it
  // asks first. An empty field has nothing to lose and goes straight through.
  const start = () => {
    if (hasProfileText && !confirming) {
      setConfirming(true);
      return;
    }
    draft.mutate();
  };

  return (
    <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 p-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={start}
          disabled={draft.isPending}
        >
          {draft.isPending ? (
            <Spinner className="h-3.5 w-3.5" />
          ) : (
            <Sparkles className="h-3.5 w-3.5" />
          )}
          {confirming ? 'Yes, replace it' : 'Draft this from past copy'}
        </Button>
        {confirming && (
          <>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setConfirming(false)}
            >
              Cancel
            </Button>
            <span className="text-[11px] text-amber-700">
              This replaces what&apos;s in the field. Nothing is saved until you save the
              account, so you can still cancel out.
            </span>
          </>
        )}
        {draft.isPending && (
          <span className="text-[11px] text-slate-500">
            Reading this brand&apos;s past campaigns… ({waiting}s)
          </span>
        )}
      </div>

      {/* ⚠️ SAYS WHAT IT WILL PRODUCE. "Drafted from past copy" naturally reads as
          "here are your best past ads", which is exactly what this must not be —
          and an AM who expects examples will think a characterisation is a bug. */}
      <p className="mt-1.5 text-[11px] text-slate-500">
        Reads this brand&apos;s past campaign copy and writes a{' '}
        <strong className="font-medium">description of how it sounds</strong> — register,
        sentence shape, punctuation, the words it uses and the ones it avoids.{' '}
        <strong className="font-medium">It never copies the ads themselves</strong>, on purpose:
        the copy generator only ever sees this description, so it has to find its own angles
        rather than restating past campaigns. Edit whatever it proposes before saving.
      </p>

      {error && (
        <p className="mt-1.5 text-xs text-amber-700">
          <AlertTriangle className="mr-1 inline h-3 w-3" />
          {error}
        </p>
      )}

      {report && !draft.isPending && (
        <p className="mt-1.5 text-[11px] text-slate-600">
          Read {report.sampledAds} {report.sampledAds === 1 ? 'piece' : 'pieces'} of copy from{' '}
          {report.sampledCampaigns}{' '}
          {report.sampledCampaigns === 1 ? 'campaign' : 'campaigns'}.
          {/* ⚠️ Thin evidence is worth saying: a voice described from four lines
              is a guess, and the AM is the one who can tell. */}
          {report.sampledAds < 6
            ? ' That is not much to go on — read it carefully before saving.'
            : ''}
          {report.referenceDoc.reason
            ? ` The ad copy doc wasn't included — ${report.referenceDoc.reason}.`
            : report.referenceDoc.read
              ? " The brand's ad copy doc was included."
              : ''}
        </p>
      )}
    </div>
  );
}
